import { ErrorHandler, Injectable, Injector, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import rg4js from 'raygun4js';
import { environment } from 'src/environments/environment';
import { IAppState } from './store/store.dto';
import { selectSystemInfo } from './store/misc/selectors';

/**
 * Initializes Raygun crash reporting + RUM. Must run before bootstrap so the
 * window.onerror handler is attached early. The old pre-rewrite UI configured
 * this in raygun.service.ts; the March 2025 rewrite dropped it, which left
 * rg4js('send'/'setUser') calls queueing forever without an API key — no UI
 * errors reached Raygun from any post-rewrite build.
 *
 * Only enabled for production builds so dev servers don't report noise.
 */
export function initRaygun(): void {
  if (!environment.production) {
    return;
  }

  rg4js('apiKey', 'OuAKhekjbLYtxIohG1uOUA');
  rg4js('enableCrashReporting', true);
  rg4js('enablePulse', true);
}

/**
 * Forwards uncaught Angular errors to Raygun (zone-trapped errors never reach
 * window.onerror, so enableCrashReporting alone misses them) and stamps every
 * report with the server version once SystemInfo arrives.
 */
@Injectable()
export class RaygunErrorHandler extends ErrorHandler {
  private readonly injector = inject(Injector);

  constructor() {
    super();

    // Resolve the Store AFTER construction: @ngrx/store-devtools' factory injects ErrorHandler,
    // so touching Store (which pulls in the devtools instrumentation) while this handler is
    // being constructed is a circular dependency (NG0200) that hangs bootstrap on the spinner.
    queueMicrotask(() => {
      this.injector
        .get<Store<IAppState>>(Store)
        .select(selectSystemInfo)
        .pipe(
          map((info) => info?.version),
          filter((version): version is string => !!version),
          distinctUntilChanged()
        )
        .subscribe((version) => rg4js('setVersion', version));
    });
  }

  override handleError(error: unknown): void {
    super.handleError(error);
    rg4js('send', { error });
  }
}
